import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Modal } from '@components/Modal';
import { trackEvent } from '@services/telemetry';
import { formatBytes } from '@utils/formatBytes';
import type { UploadLimitDetails } from '@services/UploadLimitError';
import {
  ANNUAL_SEAT_PRICE_PER_MONTH,
  FREE_MONTHLY_QUOTA_GB,
  PRO_MONTHLY_QUOTA_GB_PER_SEAT,
} from '../config/pricing';
import './UpgradePromptModal.css';

interface UpgradePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  details: UploadLimitDetails;
  isAuthenticated: boolean;
}

/**
 * The upgrade paywall shown when an upload is rejected for exceeding the monthly size allowance -
 * the single highest-intent moment in the product. Anonymous visitors see the free-account and Pro
 * plans side by side; free members see the Pro upsell. All numbers come from the server's rejection
 * payload (real usage) and the shared pricing config (no hardcoded copy). Licensed users never see
 * this modal - their path is seat adjustment, handled by the inline alert.
 */
export function UpgradePromptModal({ isOpen, onClose, details, isAuthenticated }: UpgradePromptModalProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      trackEvent('upgrade-prompt-shown', { tier: isAuthenticated ? 'free' : 'anonymous' });
    }
  }, [isOpen, isAuthenticated]);

  const requestedText = typeof details.requestedBytes === 'number' ? formatBytes(details.requestedBytes) : null;
  const usedText = typeof details.currentUsageBytes === 'number' ? formatBytes(details.currentUsageBytes) : null;
  const limitText = typeof details.limitBytes === 'number' ? formatBytes(details.limitBytes) : null;

  const handleCta = (cta: string) => {
    trackEvent('upgrade-prompt-cta', { cta, tier: isAuthenticated ? 'free' : 'anonymous' });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="upgrade-modal" ariaLabel={t('upgrade.title')}>
      <div className="upgrade-modal-body">
        <h2 className="upgrade-modal-title">{t('upgrade.title')}</h2>

        <p className="upgrade-modal-usage">
          {requestedText && <span>{t('upgrade.needs', { requested: requestedText })} </span>}
          {usedText && limitText
            ? t('upgrade.usage', { used: usedText, limit: limitText })
            : limitText
              ? t('upgrade.limitOnly', { limit: limitText })
              : null}
        </p>

        <div className="upgrade-modal-plans">
          {!isAuthenticated && (
            <div className="upgrade-plan">
              <h3 className="upgrade-plan-name">{t('upgrade.freeTitle')}</h3>
              <p className="upgrade-plan-price">{t('upgrade.freePrice')}</p>
              <ul className="upgrade-plan-features">
                <li>{t('upgrade.freeQuota', { gb: FREE_MONTHLY_QUOTA_GB })}</li>
                <li>{t('upgrade.encrypted')}</li>
              </ul>
              <Link
                to="/signup"
                className="upgrade-plan-cta upgrade-plan-cta-secondary"
                onClick={() => handleCta('signup')}
              >
                {t('upgrade.freeCta')}
              </Link>
            </div>
          )}

          <div className="upgrade-plan upgrade-plan-featured">
            <h3 className="upgrade-plan-name">{t('upgrade.proTitle')}</h3>
            <p className="upgrade-plan-price">
              {t('upgrade.proPrice', { price: ANNUAL_SEAT_PRICE_PER_MONTH })}
            </p>
            <ul className="upgrade-plan-features">
              <li>{t('upgrade.proQuota', { gb: PRO_MONTHLY_QUOTA_GB_PER_SEAT })}</li>
              <li>{t('upgrade.proDownloads')}</li>
              <li>{t('upgrade.proRetention')}</li>
              <li>{t('upgrade.encrypted')}</li>
            </ul>
            {isAuthenticated ? (
              <Link
                to="/dashboard/subscription"
                className="upgrade-plan-cta upgrade-plan-cta-primary"
                onClick={() => handleCta('subscribe')}
              >
                {t('upgrade.proCta')}
              </Link>
            ) : (
              <Link
                to="/"
                hash="pricing"
                className="upgrade-plan-cta upgrade-plan-cta-primary"
                onClick={() => handleCta('see-pricing')}
              >
                {t('upgrade.proSeeCta')}
              </Link>
            )}
          </div>
        </div>

        <button type="button" className="upgrade-modal-dismiss" onClick={onClose}>
          {t('upgrade.dismiss')}
        </button>
      </div>
    </Modal>
  );
}
